import { Injectable } from '@angular/core';
import { OrderDispensingTypeEnum } from '../enums/DispensingTypeEnum';
import { OrderSpecialRuleEnum } from '../enums/OrderSpecialRuleEnum';
import { HistOrder } from '../hist/models/hist-order';
import { WholeHist } from './api-service/hist/whole-hist';
import { DxOpt, RxOpt } from './api-service/hist/hist-edit-option';
import { NHIRegIC } from './api-service/register/nhiRegIc';
import { PatientsDto } from './api-service/patient/patientsDto';
import { HST001Params } from './api-service/parameters/HST001Params';
import { NumberValueTextPairBundle } from './data-service/clinic-data-service';
import { JailInfo } from '../hist/rating-price/models/kidneydialysisdto';
import { DateHelper } from '../shared/helpers/date-helper';
import { escapeStr, NullOrEmptyTrim, Or } from '../shared/utilities';

/** 組成衛星藥局 XML 所需的完整資料；Builder 本身不存取 API 或 Cache。 */
export interface SatellitePharmacyXmlContext {
  hist: WholeHist;
  regIc: NHIRegIC;
  patient: PatientsDto;
  app: string;
  caseMethod: string;
  jailInfo: JailInfo;
  hospId: string;
  histParams: HST001Params;
  enumCodes: NumberValueTextPairBundle;
  diagnosisOptions: DxOpt[];
  prescriptionOptions: RxOpt[];
}

/** 判斷衛星藥局 XML 是否需要輸出診藥自費拆帳金額。 */
export function hasSatelliteSplitSum(hist: WholeHist, histParams: HST001Params): boolean {
  if (!histParams || !histParams.IsAutoSplitSelfPayAccount) {
    return false;
  }

  const isTransferredOrder = (order: HistOrder) => order.RxType === 2
    || order.RxType === 3
    || ((order.RxType === 4 || order.RxType === 12)
      && order.DispTP == OrderDispensingTypeEnum.T1_OutOfClinic);

  if (hist.Register.ICode === '000') {
    return hist.Hist.HRxs.some(isTransferredOrder);
  }

  return hist.Hist.HRxs.some(order =>
    order.SPRule === OrderSpecialRuleEnum.A2_SelfPayment && isTransferredOrder(order));
}

/** 純資料轉換：將準備完成的病歷 Context 組成衛星藥局 XML。 */
@Injectable({
  providedIn: 'root'
})
export class SatellitePharmacyXmlBuilder {

  public build(context: SatellitePharmacyXmlContext): string {
    const hist = context.hist;
    const regIc = context.regIc;
    const patient = context.patient;
    const jailInfo = context.jailInfo;
    let xml = '<?xml version="1.0" encoding="big5"?>';

    const patientNo = hist.Patient.PatientNo;
    const dateStr = DateHelper.getROCDateTimeString(hist.Register.RegDate).padStart(13, '0');
    const histDate = dateStr.substring(0, 7);
    const histTime = dateStr.substring(7);
    const conPresDt = DateHelper.formatROCDate(hist.Hist.CntPresDate, false, false);
    const conPresCnt = hist.Hist.CntPresNo ?? '';
    const conPresTot = hist.Hist.CntPresTot ?? '';
    let otherMo = '';
    if (!NullOrEmptyTrim(hist.Hist.CntPresNo) && hist.Hist.CntPresNo.length > 0) {
      otherMo = hist.Register.Icno ?? '';
    }

    let preTreat = '';
    let preTime = '';
    const oweTreat = hist.Hist.LackTreatnum;
    const oweTime = hist.Hist.LackDate;
    const itreatnum = regIc.Itreatnum;
    const icdt = regIc.Icdt;
    if (hist.Register.IType === '3') {
      preTreat = oweTreat;
      preTime = oweTime == null ? '' : DateHelper.getROCDateTimeString(oweTime).padStart(13, '0');
    } else {
      preTreat = itreatnum;
      preTime = icdt == null ? '' : DateHelper.getROCDateTimeString(icdt).padStart(13, '0');
    }

    let eatAt = '';
    if (!NullOrEmptyTrim(hist.Hist.CntPresNo) && hist.Hist.CntPresNo.length > 0) {
      const eatDrugDate = Or(hist.Hist.DateStart, hist.Register.RegDate);
      eatAt = DateHelper.formatROCDate(eatDrugDate, false, false);
    }

    let tnote = '';
    if (!NullOrEmptyTrim(hist.Register.INote)) {
      tnote = escapeStr(hist.Register.INote);
    }
    const mcrp = patient.Handicapp ?? '';

    xml += `<case from="${context.hospId}" to="" local_id="${patientNo}" date="${histDate}" time="${histTime}" app="${context.app}" request_method="${context.caseMethod}">`;
    xml += this.buildProfileXml(context);
    xml += this.buildInsuranceXml(context);
    xml += `<itreatnum pretreat="${preTreat ?? ''}" pretime="${preTime}" />`;
    xml += this.buildStudyXml(context);
    xml += `<continous_prescription start_at="${conPresDt}" count="${conPresCnt}" total="${conPresTot}" other_mo="${otherMo}" eat_at="${eatAt}" />`;
    xml += `<special_status_note tnote="${tnote}" msop="" mcrp="${mcrp}" />`;
    xml += this.buildOrdersXml(context);
    xml += '</case>';
    return xml;
  }

  private buildProfileXml(context: SatellitePharmacyXmlContext): string {
    const regIc = context.regIc;
    const patient = context.patient;
    const jailInfo = context.jailInfo;
    const vitalSign = context.hist.VitalSign;
    const patientName = patient.CName;
    const cid = patient.CId;
    const foreigner = 'no';
    const sex = patient.Sex;
    const birth = DateHelper.getROCDateString(new Date(patient.Birthday)).padStart(7, '0');
    const birthNo = regIc.BirthsNum || ' ';
    const phone = patient.PhoneArea + patient.Phone;
    const mobile = patient.CellPhone;
    const email = patient.EMail;
    const blood = context.enumCodes['Blood'].find(x => x.value == patient.BlType)?.text;
    const bloodRh = context.enumCodes['RH'].find(x => x.value == patient.BlRH)?.text;
    const family = patient.Family;
    const addr = patient.City + patient.Area + patient.Street?.replace(/\u001e/g, '') || '';
    const remark = escapeStr(patient.Remark);
    const allergy = escapeStr(patient.DrugAllergy);
    const weight = this.tryParseFloat(vitalSign?.Weight);
    const temperature = this.tryParseFloat(vitalSign?.BT);
    const sbp = ((vitalSign && vitalSign?.SBP && vitalSign?.SBP > 0) ? vitalSign.SBP : '');
    const dbp = ((vitalSign && vitalSign?.DBP && vitalSign?.DBP > 0) ? vitalSign.DBP : '');
    const bloodPressure = ((!NullOrEmptyTrim(sbp.toString()) || !NullOrEmptyTrim(dbp.toString()))
      ? escapeStr([sbp, dbp].join('/')) : '');
    const jailName = escapeStr(jailInfo.JailName);
    const ward = !NullOrEmptyTrim(jailInfo.JailHouse) ? escapeStr(jailInfo.JailHouse) : '';
    const inmatesCode = !NullOrEmptyTrim(jailInfo.PensonalCode) ? escapeStr(jailInfo.PensonalCode) : '';

    let xml = '<profile>';
    xml += `<person name="${patientName}" type="0" id="${cid}" foreigner="${foreigner}" sex="${sex}" birth="${birth}" birth_order="${birthNo}" phone="${phone}" family="${family}" mobile="${mobile}" email="${email}" blood="${blood}" blood_rh="${bloodRh}">`;
    xml += `<addr><![CDATA[${addr}]]></addr>`;
    xml += `<remark><![CDATA[${remark}]]></remark>`;
    xml += `<allergy><![CDATA[${allergy}]]></allergy>`;
    xml += `<weight><![CDATA[${weight}]]></weight>`;
    xml += `<temperature><![CDATA[${temperature}]]></temperature>`;
    xml += `<blood_pressure><![CDATA[${bloodPressure}]]></blood_pressure>`;
    xml += '</person>';
    let organizations = `<organizations prison_code="${jailInfo.JailAddressCode}" prison_name="${jailName}">`;
    organizations += `<ward><![CDATA[${ward}]]></ward>`;
    organizations += `<inmates_code><![CDATA[${inmatesCode}]]></inmates_code>`;
    organizations += '</organizations>';
    xml += organizations;
    xml += '</profile>';
    return xml;
  }

  private buildInsuranceXml(context: SatellitePharmacyXmlContext): string {
    const regIc = context.regIc;
    const hist = context.hist;
    const insuranceType = hist.Register.ICode.replace('00', '').replace('0', '');
    const isqno = NullOrEmptyTrim(regIc.Ierrcd)
      ? (NullOrEmptyTrim(regIc.Isqno) ? regIc.IsqnoOri : regIc.Isqno)
      : regIc.Ierrcd;
    const icNo = isqno ?? '';
    const apartCode = hist.Hist.ApartCode;
    const caseTp = hist.Hist.Case;
    const payTp = hist.Hist.LABTP;
    const dispTp = hist.Hist.DispTP;
    const ldisTp = hist.Hist.PayTP;
    let labeno = '';
    if (!NullOrEmptyTrim(hist.Register.INote)) {
      labeno = escapeStr(hist.Register.INote);
    }
    const sumAmt = hist.Hist.AOwnRx;
    let xml = `<insurance insurance_type="${insuranceType}" serial_code="${icNo}" except_code="" copayment_code="${apartCode}" case_type="${caseTp}" pay_type="${payTp}" ldistp_type="${ldisTp}" release_type="${dispTp}" labeno_type="${labeno}" `;
    if (hasSatelliteSplitSum(hist, context.histParams)) {
      xml += `sum_amt="${sumAmt}" `;
    }
    xml += '/>';
    return xml;
  }

  private buildStudyXml(context: SatellitePharmacyXmlContext): string {
    const hist = context.hist;
    let doctorCId = hist.Register.DoctorCId;
    const subject = hist.Register.MedDeptCode;
    if (!context.histParams.SatellitePhamacyDoctorCId) {
      doctorCId = context.hospId;
    }

    const cc = escapeStr(hist.Hist.CC);
    const pe = escapeStr(hist.Hist.PE);
    let xml = `<study doctor_id="${doctorCId}" subject="${subject}">`;
    xml += '<diseases>';
    const diagnoses = [hist.Hist.Dx1, hist.Hist.Dx2, hist.Hist.Dx3];
    diagnoses.filter(x => x).forEach(code => {
      const diagnosis = context.diagnosisOptions.find(x => x.Code == code);
      const diagnosisName = escapeStr(diagnosis.CName);
      xml += `<item code="${diagnosis.ApplyCode}" type="ICD10" desc="${diagnosisName}" />`;
    });
    xml += '</diseases>';
    xml += '<treatments>';
    const treatments = [hist.Hist.SPTX1, hist.Hist.SPTX2, hist.Hist.SPTX3, hist.Hist.SPTX4];
    treatments.filter(x => x).forEach(code => {
      xml += `<item code="${code}" desc="" />`;
    });
    xml += '</treatments>';
    xml += `<chief_complain><![CDATA[${cc}]]></chief_complain>`;
    xml += `<physical_examination><![CDATA[${pe}]]></physical_examination>`;
    xml += '</study>';
    return xml;
  }

  private buildOrdersXml(context: SatellitePharmacyXmlContext): string {
    const hist = context.hist;
    const days = hist.Hist.Days;
    const mill = hist.Hist.HRxs.find(x => x.Remark.indexOf('磨粉') > -1) ? 'yes' : 'no';
    const dosageMethod = context.histParams.DosageRule === '1' ? '2'
      : context.histParams.DosageRule === '2' ? '4'
      : context.histParams.DosageRule === '3' ? '5' : '2';
    let xml = `<orders days="${days}" mill="${mill}" dosage_method="${dosageMethod}">`;

    hist.Hist.HRxs.forEach(order => {
      const specialRemark = (order.DispTP == OrderDispensingTypeEnum.T1_OutOfClinic)
        && (order.RxType === 4 || order.RxType === 12)
        && (order.SPRule !== 2 && order.SPRule !== 3 && order.SPRule !== 4) ? '$'
        : order.SPRule === 1 ? '+'
        : order.SPRule === 2 ? '*'
        : order.SPRule === 3 ? '-'
        : order.SPRule === 4 ? '-'
        : '';
      const dosageRemark = this.parseDosageRemark(order.DosageRemark ?? '');
      const dosageMemo = !NullOrEmptyTrim(dosageRemark) ? dosageRemark + '  ' + order.Remark : order.Remark;
      const localCode = escapeStr(order.RxCode);
      const id = context.prescriptionOptions.find(x => x.RxCode == order.RxCode).StdCode;
      const day = order.Days === 0 ? order.InfoDays : order.Days;
      const price = order.SPRule === 2 ? order.Price : order.IPrice;
      const multiplier = order.Plus ?? '1.00';
      const memo = escapeStr(dosageMemo);
      const name = escapeStr(order.ProdName);
      const unit = !NullOrEmptyTrim(order.Unit) ? escapeStr(order.Unit) : '';
      const controlDrug = !NullOrEmptyTrim(order.Rx?.CTRDrugs) ? escapeStr(order.Rx?.CTRDrugs) : '';

      xml += `<item remark="${specialRemark}" local_code="${localCode}" id="${id}" nowid="${id}" type="${order.RxType}" `;
      xml += `clinic_divided_dose="${order.Dose}" divided_dose="${order.Dose}" daily_dose="${order.DailyDose}" total_dose="${order.TotalDose}" `;
      xml += `freq="${order.Freq}" days="${day}" way="${order.Way}" `;
      xml += `price="${price ?? '0'}" multiplier="${multiplier}" memo="${memo}" desc="${name}" dum="${unit}" dtp1="${controlDrug}" />`;
    });
    xml += '</orders>';
    return xml;
  }

  private tryParseFloat(value: number | string): string {
    if (!value) {
      return '';
    }
    const result = parseFloat(value as string);
    return isNaN(result) || result <= 0 ? '' : result.toFixed(1).toString();
  }

  private parseDosageRemark(dosageRemark: string): string {
    if (NullOrEmptyTrim(dosageRemark)) {
      return '';
    }
    const dosageRow: Record<string, number> = JSON.parse(dosageRemark);
    const dosageMap: Record<string, string> = {
      M: '早',
      A: '午',
      E: '晚',
      N: '睡前'
    };
    return Object.entries(dosageRow)
      .filter(([_, value]) => value !== 0)
      .map(([key, value]) => `${dosageMap[key]}:${value}`)
      .join('，');
  }
}
